import { Button } from '@/components/ui/atoms/button'
import { navigate } from '@/lib/client'
import { cn, getPrice } from '@/lib/client'
import type { ICombo, ICourse, IModule } from '@/types/product'
import { useStore } from '@nanostores/react'
import { ShoppingBag } from 'lucide-react'
import { atom } from 'nanostores'
import type React from 'react'
import { useEffect, useRef } from 'react'

import Section from '@/components/ui/atoms/Section'
import { CardCarousel } from '@/components/ui/molecules/card-carousel'

import SubscriptionBanner from '@/components/core/shared/subscription.banner'
import { actions } from '@/store/checkout'
import { AddToCart } from '@/tracking/cart'
import { ListImpression, PageImpression } from '@/tracking/impression'
import type { ICheckoutOrderSummaryItem } from '@/types/checkout'
import type { IPaymentStandard } from '@/types/payment'
import type { ITeacher } from '@/types/teacher'
import TeachersListItem from '../teachers/teachers.list.item'
import ProductDetailsPrice from './product.details.price'
import ProductRelatedMaterial from './product.related.material'

const seeFixedFooterAtom = atom<boolean>(false)

export default function ProductDetailsContent({
  product,
  payment,
}: {
  product: ICourse | ICombo | IModule
  payment: IPaymentStandard
}) {
  const fixedFooterRef = useRef<HTMLElement | null>(null)
  const contentRef = useRef<HTMLElement | null>(null)
  const $seeFixedFooter = useStore(seeFixedFooterAtom)

  const total = product.children.some((p) => p.price)
    ? (product.children as { price: number }[]).reduce((acc, item) => {
        return acc + Number(item.price)
      }, 0)
    : 0

  const from = product.discountedPrice ? product.price : null
  const to =
    (product.discountedPrice || product.price) *
    (1 - (payment?.in_cash?.greater || 0) / 100)

  useEffect(() => {
    PageImpression({
      Produto: product.name,
      ProdutoID: product._id,
      Tipo:
        product.type === 'course'
          ? 'Curso'
          : product.type === 'combo'
            ? 'Combo'
            : 'Módulo',
      Valor: from || product.price,
      Instituicao: product.publicTender.name,
      Carreira: (product?.publicTender?.career || []).join('/'),
      Cargo: product?.publicTender?.role,
      Formacao: product?.publicTender?.formation,
      Edital: product?.publicTender?.situation,
      Desconto: to || 0,
    })

    const type: 'Módulo' | 'Curso' =
      product.type === 'course'
        ? 'Módulo'
        : product.type === 'combo'
          ? 'Curso'
          : 'Módulo'

    const items = product.children
      .filter((p) => p._id)
      .map(
        (item: {
          name: string
          _id: string
          price: number
          discount: number
        }) => ({
          Produto: item.name,
          ProdutoID: item._id,
          Tipo: type,
          Valor: item.price,
          Desconto: item.discount,
          Instituicao: product.publicTender.name,
          Carreira: (product?.publicTender?.career || []).join('/'),
          Cargo: product?.publicTender?.role,
          Formacao: product?.publicTender?.formation,
          Edital: product?.publicTender?.situation,
        }),
      )

    ListImpression({
      type,
      items,
    })
  }, [])

  useEffect(() => {
    const handleScroll = (): void => {
      if (
        window.scrollY > 850 &&
        contentRef.current &&
        window.scrollY <= contentRef.current.offsetHeight
      ) {
        seeFixedFooterAtom.set(true)
      } else {
        seeFixedFooterAtom.set(false)
      }
    }

    window.addEventListener('scroll', handleScroll)
    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [fixedFooterRef, seeFixedFooterAtom])

  const slideContent = (item: ITeacher) => {
    return <TeachersListItem teacher={item} />
  }

  const handleClick = (ev: React.MouseEvent<HTMLElement>) => {
    ev.preventDefault()
    const item: ICheckoutOrderSummaryItem = {
      _id: product._id,
      name: product.name,
      discountedPrice: product.discountedPrice,
      price: product.price,
      quantityInstallments: product.quantityInstallments,
      type: product.type,
      icon: product.icon,
      percentage_off: product.percentage_off,
      installmentValue: product.installmentValue,
      discountType: null,
      institution: product.publicTender.name,
      career: (product?.publicTender?.career || []).join('/'),
      role: product?.publicTender?.role,
      formation: product?.publicTender?.formation,
      situation: product?.publicTender?.situation,
      quantity: 1,
    }
    actions.add(item)
    AddToCart({
      Produto: product.name,
      ProdutoID: product._id,
      Tipo:
        product.type === 'course'
          ? 'Curso'
          : product.type === 'combo'
            ? 'Combo'
            : 'Módulo',
      Valor: from || product.price,
      Instituicao: product.publicTender.name,
      Carreira: (product?.publicTender?.career || []).join('/'),
      Cargo: product?.publicTender?.role,
      Formacao: product?.publicTender?.formation,
      Edital: product?.publicTender?.situation,
      Desconto: to || 0,
    })
    navigate('/carrinho')
  }

  return (
    <Section id={`${product.type}-content`}>
      <section
        className="flex justify-between gap-x-4 lg:gap-x-8 flex-col-reverse md:flex-row"
        ref={contentRef}
      >
        <div className="prose lg:prose-lg max-w-[40ch] lg:max-w-[70ch] prose-zinc">
          <h2>{product.publicTender.role}</h2>
          <div
            className="mt-5"
            dangerouslySetInnerHTML={{
              __html: product.description || '',
            }}
          />
          <h2>Confira os professores</h2>
          <p>
            Os professores do Direção são aprovados nos concursos mais difíceis
            do país.
          </p>
          <CardCarousel
            elements={product.teachers}
            id={'name'}
            slideContent={slideContent}
          />

          {product.children.length > 0 && (
            <>
              <h2>Material do curso</h2>
              <ProductRelatedMaterial
                product={product}
                total={total}
                to={to}
                handleClick={handleClick}
              />
            </>
          )}
          <SubscriptionBanner className="not-prose" />
        </div>

        <aside className="sm:h-screen sm:-mt-48 sm:sticky sm:top-32 mb-6">
          <ProductDetailsPrice
            product={product}
            from={from}
            to={to}
            handleClick={handleClick}
          />
        </aside>

        <section
          className={cn(
            'fixed bottom-0 left-0 z-10 bg-white w-full border-t md:hidden',
            !$seeFixedFooter && 'hidden',
          )}
          ref={fixedFooterRef}
        >
          <div className="flex flex-col gap-2 p-4 text-center">
            <div>
              {from ? (
                <div className="flex flex-col text-sm font-light">
                  <span className="text-zinc-500">
                    De <span className="line-through">{getPrice(from)}</span>
                  </span>
                  <span>Em até {product.quantityInstallments}x de </span>
                  <strong
                    className={cn(
                      'text-2xl font-bold',
                      product.percentage_off && 'text-emerald-500',
                    )}
                  >
                    {getPrice(product.installmentValue)}
                  </strong>
                </div>
              ) : (
                <div className="flex flex-col">
                  <span className="text-xs leading-3">
                    Em até {product.quantityInstallments}x de
                  </span>
                  <strong
                    className={cn(
                      'text-2xl font-bold',
                      product.percentage_off && 'text-emerald-500',
                    )}
                  >
                    {getPrice(product.installmentValue)}
                  </strong>
                </div>
              )}
              <span className="text-xs">
                Ou{' '}
                <strong
                  className={cn(product.percentage_off && 'text-emerald-500')}
                >
                  {getPrice(to)}
                </strong>{' '}
                à vista com desconto
              </span>
            </div>

            <Button
              size={'sm'}
              className="rounded-sm w-full"
              onClick={handleClick}
            >
              <ShoppingBag size={18} className="mr-2" /> Comprar
            </Button>
          </div>
        </section>
      </section>
    </Section>
  )
}
